// pkg172a — "Send LawPay Link" / "Request Retainer via LawPay" button + modal (lane 2, D-172d revised): staff picks amount + recipient, the backend builds the merchant's hosted page URL with amount+reference locked and emails it in the firm shell; hidden entirely when LawPay isn't connected or the needed slot/page is missing — delivered 2026-10-03
import React, { useEffect, useState } from 'react';
import { adminApi } from '../utils/adminApi';

const money = n => '$' + (+n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const F = { fontFamily: 'Arial, sans-serif' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflow: 'auto' };
const box = { background: '#fff', borderRadius: 6, width: '100%', maxWidth: 520, padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.3)', ...F };

// purpose 'invoice' needs invoiceId + balanceDue; 'retainer' needs only clientId.
export default function LawPayLinkButton({ clientId, clientEmail, invoiceId, invoiceNumber, balanceDue, purpose = 'invoice', small = false, onSent }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => { adminApi.lawpayStatus().then(setStatus).catch(() => setStatus(null)); }, []);

  if (!status || !status.connected) return null;
  const slotReady = purpose === 'retainer' ? status.ready.trust : status.ready.operating;
  const pageReady = purpose === 'retainer' ? (status.pages_ready || {}).trust : (status.pages_ready || {}).operating;
  if (!slotReady || !pageReady) return null;
  if (purpose === 'invoice' && !(parseFloat(balanceDue) > 0)) return null;

  function start() {
    setErr(''); setDone(null);
    setAmount(purpose === 'invoice' ? (+balanceDue).toFixed(2) : '');
    setTo(clientEmail || '');
    setMessage('');
    setOpen(true);
  }

  async function send() {
    setBusy(true); setErr('');
    try {
      const r = await adminApi.createPaymentRequest({
        client_id: clientId, invoice_id: purpose === 'invoice' ? invoiceId : undefined, purpose,
        amount: amount === '' ? undefined : +amount, to_email: to || undefined, message: message || undefined, send: true,
      });
      setDone(r);
      if (onSent) onSent(r);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const label = purpose === 'retainer' ? 'Request Retainer via LawPay' : 'Send LawPay Link';
  const testNote = status.mode === 'test' ? ' (TEST MODE — no real money moves)' : '';
  return (
    <>
      <button type="button" className={`btn ${small ? 'btn-sm' : ''}`} onClick={start} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <img src="/lawpay-logo.svg" alt="" style={{ height: 14, width: 'auto' }} />{label}
      </button>
      {open && (
        <div style={overlay} onClick={e => { if (e.target === e.currentTarget && !busy) setOpen(false); }}>
          <div style={box}>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>{label}</h2>
            <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
              {purpose === 'retainer'
                ? `The client receives an email with a LawPay link to your trust payment page. The amount is locked. The deposit lands in your Trust account and on the client's IOLTA ledger once LawPay settles it${testNote}.`
                : `The client receives an email with a LawPay link to your operating payment page for invoice #${invoiceNumber}. The amount is locked; the payment lands on the invoice once LawPay settles it${testNote}.`}
            </p>
            {done ? (
              <>
                <div className="alert alert-success">Link {done.sent_count ? `emailed to ${done.to_email}` : 'created'} — reference <strong>{done.reference}</strong> for {money(done.amount)}.</div>
                <div style={{ fontSize: 12, wordBreak: 'break-all', background: '#f6f6f6', padding: 8, borderRadius: 4 }}>{done.url}</div>
                <div className="btn-row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
                  <button className="btn" onClick={() => navigator.clipboard && navigator.clipboard.writeText(done.url)}>Copy link</button>
                  <button className="btn btn-primary" onClick={() => setOpen(false)}>Done</button>
                </div>
              </>
            ) : (
              <>
                {err && <div className="alert alert-error">{err}</div>}
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount{purpose === 'invoice' ? ` (balance ${money(balanceDue)})` : ''}</label>
                    <input type="number" min="1" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Send to</label>
                    <input type="email" value={to} onChange={e => setTo(e.target.value)} placeholder="client@example.com" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Message (optional, appears above the link)</label>
                  <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} />
                </div>
                <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
                  <button className="btn btn-primary" onClick={send} disabled={busy || !(parseFloat(amount) > 0) || !to}>
                    {busy ? <><span className="spinner" />Sending…</> : 'Send Email'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
